import { Info } from "lucide-react";

import { cn } from "@/lib/utils";

export interface PickerOption<T extends string = string> {
  value: T;
  label: string;
  /** What choosing this actually does. Shown for the selected option only. */
  description?: string;
}

export interface OptionPickerProps<T extends string = string> {
  value: T;
  options: readonly PickerOption<T>[];
  onChange: (value: T) => void;
  /** Tone for the selected pill. Defaults to brand. */
  tone?: "brand" | "danger" | "warning" | "success";
  /** Hide the description line even when the active option has one. */
  hideDescription?: boolean;
  disabled?: boolean;
  className?: string;
  /** Accessible name for the group. */
  label?: string;
}

const TONE_CLASS: Record<string, string> = {
  brand: "border-brand bg-brand/10 text-brand",
  danger: "border-danger bg-danger/10 text-danger",
  warning: "border-warning bg-warning-light text-warning",
  success: "border-success bg-success-light text-success",
};

/**
 * A small set of mutually exclusive choices as pills, with a line explaining
 * what the SELECTED one means.
 *
 * Built for choices where the options aren't self-explanatory and picking the
 * wrong one has consequences — planning states, for instance, where three of
 * four behave identically and only one changes the maths. A plain `Select`
 * hides that; a radio group makes the caller find somewhere to put the
 * explanation. This keeps the consequence next to the choice.
 *
 * Use a `Select` instead when there are more than about five options, or when
 * the labels already say everything.
 */
export function OptionPicker<T extends string = string>({
  value,
  options,
  onChange,
  tone = "brand",
  hideDescription = false,
  disabled = false,
  className,
  label,
}: OptionPickerProps<T>) {
  const active = options.find((o) => o.value === value);
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label={label}>
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={value === o.value}
            disabled={disabled}
            onClick={() => onChange(o.value)}
            className={cn(
              "rounded-md border px-2.5 py-1 text-xs font-medium disabled:opacity-50",
              value === o.value
                ? (TONE_CLASS[tone] ?? TONE_CLASS.brand)
                : "border-border text-muted-foreground hover:bg-muted",
            )}
          >
            {o.label}
          </button>
        ))}
      </div>
      {!hideDescription && active?.description && (
        <p className="flex items-start gap-1 text-[11px] text-muted-foreground">
          <Info className="mt-px h-3 w-3 shrink-0" />
          {active.description}
        </p>
      )}
    </div>
  );
}
